<template>
	<div class="container_section p-5">
		<div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-4">
			<div class="is-flex is-align-items-center">
				<Ecs class="mr-2" style="width: 20px; height: 20px;" />
				<span class="tittle_section">Evolución de Ventas Netas</span>
			</div>
			<div class="table_actions is-flex">
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Comparación
				</button>
				<button class="action_btn mr-2">
					<ChartLine class="mr-1" style="width: 16px; height: 16px;" />
					Ver Gráfico
				</button>
				<div v-if="showPeriodSelect" style="min-width: 140px;">
					<SelectComponent :value="selectedPeriod" theme="report_theme"
						:options="periodOptions"
						@input="$emit('change-period', $event)" />
				</div>
			</div>
		</div>

		<section>
			<div v-if="isLoading" class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>

			<div v-else-if="error" class="error_state py-6 has-text-centered">
				<p class="has-text-danger">{{ error }}</p>
				<button class="action_btn mt-3" @click="$emit('retry')">Reintentar</button>
			</div>

			<div v-else-if="monthHeaders.length === 0" class="empty_state py-6 has-text-centered">
				<p>No hay datos de ventas para el período seleccionado.</p>
			</div>

			<div v-else class="table_container" ref="mainTable">
				<table class="custom_table"
					:style="{ '--sticky-col-1-width': firstColWidth + 'px' }">
					<colgroup>
						<col style="width: 200px;" />
						<col style="width: 160px;" />
						<col style="width: 160px;" v-for="header in monthHeaders" :key="header" />
					</colgroup>
					<thead>
						<tr>
							<th ref="firstStickyCell" scope="col" class="sticky-column header_label nobreak"></th>
							<th scope="col"
								class="sticky-column sticky-column--acc header_label has-text-right nobreak">
								Acumulado
							</th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in monthHeaders" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in tableRows" :key="row.label" class="theme_row_white">
							<th scope="row" class="sticky-column nobreak theme_row_white is-capitalized">
								<span v-if="row.label.length > 30"
									class="has-tooltip-right"
									:data-tooltip="row.label">
									{{ row.label.substring(0, 30) + '...' }}
								</span>
								<span v-else>
									{{ row.label }}
								</span>
							</th>
							<td class="sticky-column sticky-column--acc has-text-right nobreak acc_cell theme_row_white">
								<span class="acc_amount">{{ parseToMoney(row.accumulated) }}</span>
								<span class="acc_pct" :class="pctClass(row.pct)">
									{{ formatPct(row.pct) }}
								</span>
							</td>
							<td v-for="(val, idx) in row.values" :key="idx"
								class="has-text-right nobreak theme_row_white">
								{{ parseToMoney(val) }}
							</td>
						</tr>

						<tr class="theme_row_azure">
							<th scope="row" class="sticky-column nobreak theme_row_azure">
								{{ totalRow.label }}
							</th>
							<td class="sticky-column sticky-column--acc has-text-right nobreak acc_cell theme_row_azure">
								<span class="acc_amount">{{ parseToMoney(totalRow.accumulated) }}</span>
								<span class="acc_pct" :class="pctClass(totalRow.pct)">
									{{ formatPct(totalRow.pct) }}
								</span>
							</td>
							<td v-for="(val, idx) in totalRow.values" :key="'total-' + idx"
								class="has-text-right nobreak theme_row_azure">
								{{ parseToMoney(val) }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import SelectComponent from "@/components/classification/SelectComponent";
import Ecs from "@/components/svg/Ecs.vue";
import ChartLine from "@/components/svg/ChartLine.vue";

export default {
	name: "SalesEvolutionTable",

	components: { SelectComponent, Ecs, ChartLine },

	props: {
		monthHeaders: { type: Array, required: true },
		tableRows: { type: Array, required: true },
		totalRow: { type: Object, required: true },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		selectedPeriod: { type: String, required: true },
		periodOptions: { type: Array, required: true },
		// Granularidad activa (month/quarter/year/ytd). Cuando es year/ytd el
		// rango ya es implícito y ocultamos el selector de períodos — mismo
		// criterio que FinancialResultsTable.
		filterPeriod: { type: String, default: "month" },
	},

	computed: {
		showPeriodSelect() {
			return !["ytd", "year"].includes(this.filterPeriod);
		},
	},

	data() {
		// Fallback inicial igual al <col width> de la primera columna para evitar
		// "flash" antes de la primera medición. Se sobrescribe en mounted().
		return { firstColWidth: 200 };
	},

	mounted() {
		// La primera medición y el observer corren después del render para que
		// el thead exista. ResizeObserver dispara también con cambios indirectos
		// (filtros, scrollbars, zoom del navegador) sin necesidad de watchers.
		this.$nextTick(() => {
			this.measureFirstCol();
			this.attachResizeObserver();
		});
	},

	beforeDestroy() {
		if (this._resizeObserver) {
			this._resizeObserver.disconnect();
			this._resizeObserver = null;
		}
	},

	methods: {
		parseToMoney,
		scrollToRight() {
			this.$nextTick(() => {
				const table = this.$refs.mainTable;
				if (table) table.scrollLeft = table.scrollWidth;
			});
		},
		// Lee el ancho real de la primera columna y lo expone como CSS var en
		// la <table>; .sticky-column--acc usa esa var como offset `left`, así
		// la segunda sticky queda pegada al borde derecho de la primera sin
		// importar cuánto crezca el contenido de la cuenta.
		measureFirstCol() {
			const cell = this.$refs.firstStickyCell;
			if (cell) this.firstColWidth = cell.offsetWidth;
		},
		attachResizeObserver() {
			if (typeof ResizeObserver === "undefined") return;
			const cell = this.$refs.firstStickyCell;
			if (!cell) return;
			// Disconnect previo: v-if del table_container desmonta/remonta el
			// thead, así que el ref puede apuntar a un nodo nuevo entre llamadas.
			if (this._resizeObserver) this._resizeObserver.disconnect();
			this._resizeObserver = new ResizeObserver(() => this.measureFirstCol());
			this._resizeObserver.observe(cell);
		},
		// pct viene como ratio (0.123 = +12,3%), null cuando no hay comparable.
		// Formato chileno: coma decimal, una cifra, flecha según signo.
		formatPct(pct) {
			if (pct == null) return "—";
			const symbol = pct > 0 ? "▲" : pct < 0 ? "▼" : "";
			const value = (Math.abs(pct) * 100).toFixed(1).replace(".", ",");
			const sign = pct > 0 ? "+" : pct < 0 ? "-" : "";
			return symbol ? `${symbol} ${sign}${value}%` : `${value}%`;
		},
		pctClass(pct) {
			if (pct == null) return "acc_pct--neutral";
			if (pct > 0) return "acc_pct--positive";
			if (pct < 0) return "acc_pct--negative";
			return "acc_pct--neutral";
		},
	},

	watch: {
		monthHeaders() {
			this.scrollToRight();
			// Cuando cambia el periodo, los account names del breakdown pueden
			// cambiar y el thead se desmonta-remonta — re-attachear el observer.
			this.$nextTick(() => {
				this.measureFirstCol();
				this.attachResizeObserver();
			});
		},
		isLoading(val) {
			if (!val) {
				this.scrollToRight();
				this.$nextTick(() => {
					this.measureFirstCol();
					this.attachResizeObserver();
				});
			}
		},
	},
};
</script>

<style scoped lang="scss">
.container_section {
	background: var(--report-bg);
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text);
}

.action_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}
}

.table_container {
	// Scroll en ambos ejes: x para el ancho del periodo, y para listas largas.
	overflow: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	background: var(--report-bg);
	max-width: 100%;
	// Alto máx ≈ thead (44) + 12 filas (12*44) + totales (44) = 616px.
	// max-height (no height) → si hay menos de 12 filas, la tabla se ajusta.
	max-height: calc(44px * 12);
	// Aísla este subárbol del resto del documento — repaints/reflows no se propagan.
	contain: layout paint;
}

// Primera columna sticky: ancho flexible. El <col width: 200px> queda como
// hint inicial en auto-layout (puede crecer si el contenido es más largo).
.sticky-column {
	position: sticky;
	left: 0;
	z-index: 2;
	background-color: inherit;
	border-right: 1px solid var(--report-border);
}

// Segunda columna sticky (Acumulado): el offset `left` se calcula en runtime
// midiendo el ancho real de la primera columna con ResizeObserver y
// exponiéndolo como --sticky-col-1-width en el style inline de la <table>.
// El fallback 200px coincide con el hint del <col> para el primer paint.
.sticky-column--acc {
	left: var(--sticky-col-1-width, 200px);
	box-sizing: border-box;
	// width: 160px;
	// min-width: 160px;
	// max-width: 160px;
}

// Celda apilada: monto arriba (énfasis), pct abajo (caption con flecha).
// Display block en los spans para forzar el wrap vertical sin <br>.
.acc_cell {
	.acc_amount {
		display: block;
		color: var(--report-text);
		font-size: 14px;
		font-weight: 500;
		line-height: 18px;
	}

	.acc_pct {
		display: block;
		font-size: 12px;
		font-weight: 500;
		line-height: 16px;
		margin-top: 2px;
	}

	.acc_pct--positive { color: var(--report-positive); }
	.acc_pct--negative { color: var(--report-negative); }
	.acc_pct--neutral  { color: var(--report-text-muted); }
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	tbody tr {
		// Cada fila aislada: cambios en una fila no fuerzan layout del resto.
		contain: layout style;
	}

	td,
	th {
		height: 44px;
		padding: 12px 16px !important;
		border-bottom: 1px solid var(--report-divider-soft);
		vertical-align: middle;
	}

	thead th,
	td {
		text-align: end;
	}

	thead th {
		padding: 12px 24px;
		color: var(--report-text-muted);
		font-size: 12px;
		font-weight: 700;
		line-height: 18px;
		background-color: var(--report-surface);
		// Sticky-top: el encabezado queda visible al scrollear verticalmente.
		// El background-color es indispensable: como sticky pinta fuera del
		// flujo normal, sin él se vería el contenido del body atrás.
		position: sticky;
		top: 0;
		z-index: 3;
	}

	tbody th {
		color: var(--report-text);
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
	}

	tbody td {
		color: var(--report-text-muted-strong);
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
	}

	.theme_row_white {
		background-color: var(--report-bg);
	}

	.header_label {
		background-color: var(--report-surface);
	}

	.theme_row_azure {
		background-color: var(--report-row-azure);
		font-weight: 600;

		// Sticky-bottom: la fila de totales (Ventas Netas) queda anclada al
		// borde inferior del scroll-container. Aplicado a las celdas (no al
		// <tr>) porque algunos engines no soportan sticky en filas de tabla.
		th, td {
			position: sticky;
			bottom: 0;
			z-index: 3;
		}
	}

	// Esquinas: las celdas que son sticky en AMBOS ejes (top + left) deben
	// pintar por encima de los stickies de un solo eje, por eso suben a z-index
	// 4. Incluye la nueva columna "Acumulado" (sticky-column--acc) tanto en el
	// thead como en la fila de totales (theme_row_azure).
	thead .sticky-column,
	thead .sticky-column--acc,
	.theme_row_azure .sticky-column,
	.theme_row_azure .sticky-column--acc {
		z-index: 4;
	}
}

.loading_state,
.empty_state,
.error_state {
	color: var(--report-text-muted);
	font-size: 14px;
}

.nobreak {
	white-space: nowrap;
}
</style>
